import { AavoMosaic } from "src/components/common/mosaic/AavoMosaic.tsx";
import { CustomersDataGrid } from "src/components/views/erp/sales/customer/CustomersDataGrid.tsx";
import { CustomerAddressDataGrid } from "src/components/views/erp/sales/customer/CustomerAddressDataGrid.tsx";
import { type ReactElement, useState } from "react";
import { CustomerView } from "src/api/generated/erp/db/types/tables/customerView.ts";
import { CenteredTypography } from "src/components/common/CenteredTypography.tsx";
import i18n from "i18next";
import { ContactsDataGrid } from "src/components/views/erp/contacts/ContactsDataGrid.tsx";
import { SalesLeadsDataGrid } from "src/components/views/erp/sales/crm/salesLeads/SalesLeadsDataGrid.tsx";
import { CustomerDeliveriesDataGrid } from "src/components/views/erp/sales/customer/CustomerDeliveriesDataGrid.tsx";
import { CustomerInvoicesDataGrid } from "src/components/views/erp/sales/customer/CustomerInvoicesDataGrid.tsx";
import { CustomerDeliveryPackagesView } from "src/components/views/erp/sales/customer/CustomerDeliveryPackagesView.tsx";
import { OrdersOfCustomerDataGrid } from "src/components/views/erp/sales/customer/OrdersOfCustomerDataGrid.tsx";
import { OffersOfCustomerDataGrid } from "src/components/views/erp/sales/customer/OffersOfCustomerDataGrid.tsx";
import { ReclamationsOfCustomerDataGrid } from "src/components/views/erp/sales/customer/ReclamationsOfCustomerDataGrid.tsx";
import { ProjectsOfCustomerDataGrid } from "src/components/views/erp/sales/customer/ProjectsOfCustomerDataGrid.tsx";
import { Permissions } from "src/api/generated/iam/permissions.ts";
import { useHasPermission } from "src/components/views/erp/common/userPermissions.ts";
import { useTenantCustomizations } from "src/tenantCustomizations/TenantCustomizationsContext.ts";
import { useTasksEnabled } from "src/components/views/tasks/useTasksEnabled.ts";
import { SingleSourceTasksView } from "src/components/views/tasks/pages/SingleSourceTasksView.tsx";

export interface CustomersViewProps {
	onlyCustomerToShowId?: number;
	isActive?: boolean;
}

export const CustomersView = ({ onlyCustomerToShowId }: CustomersViewProps) => {
	const hasPermission = useHasPermission();
	const { tenantConfig } = useTenantCustomizations();
	const tasksEnabled = useTasksEnabled();
	const [selectedCustomer, setSelectedCustomer] = useState<CustomerView | undefined>(undefined);
	const canReadSalesLeads = hasPermission(Permissions.Sales.SalesLeads.READ);
	const selectedCustomerContent = (render: (customerId: number) => ReactElement) =>
		selectedCustomer == undefined ?
			<CenteredTypography>{i18n.t("select_customer")}</CenteredTypography>
		:	render(selectedCustomer.customerId);

	return (
		<AavoMosaic
			layout={{
				type: "column",
				first: {
					type: "panel",
					content: (
						<CustomersDataGrid
							onlyCustomerToShowId={onlyCustomerToShowId}
							onSelectionChanged={setSelectedCustomer}
						/>
					),
				},
				second: {
					type: "tabs",
					items: [
						hasPermission(Permissions.Sales.Customers.READ) && {
							type: "panel",
							title: i18n.t("addresses"),
							content: selectedCustomerContent((customerId) => (
								<CustomerAddressDataGrid key={customerId} customerId={customerId} />
							)),
						},
						hasPermission(Permissions.Sales.Customers.READ) && {
							type: "panel",
							title: i18n.t("contacts"),
							content: selectedCustomerContent((customerId) => (
								<ContactsDataGrid
									key={customerId}
									connectedItemType={"CUSTOMER"}
									connectedItemId={customerId}
									editable={hasPermission(Permissions.Sales.Customers.MANAGE)}
								/>
							)),
						},
						hasPermission(Permissions.Sales.Customers.READ) && tasksEnabled && {
							type: "panel",
							title: i18n.t("tasks"),
							content: selectedCustomerContent((customerId) => (
								<SingleSourceTasksView
									key={customerId}
									taskSourceRef={{ sourceType: "CUSTOMER", sourceId: customerId }}
								/>
							)),
						},
						tenantConfig.erp.crmEnabled && canReadSalesLeads && {
							type: "panel",
							title: i18n.t("sales_leads"),
							content: selectedCustomerContent((customerId) => (
								<SalesLeadsDataGrid key={customerId} customerId={customerId} />
							)),
						},
						hasPermission(Permissions.Sales.CustomerOffers.READ) && {
							type: "panel",
							title: i18n.t("offers"),
							content: selectedCustomerContent((customerId) => (
								<OffersOfCustomerDataGrid key={customerId} customerId={customerId} />
							)),
						},
						hasPermission(Permissions.Sales.CustomerOrders.READ) && {
							type: "panel",
							title: i18n.t("customer_orders"),
							content: selectedCustomerContent((customerId) => (
								<OrdersOfCustomerDataGrid key={customerId} customerId={customerId} />
							)),
						},
						hasPermission(Permissions.Projects.READ) && {
							type: "panel",
							title: i18n.t("projects"),
							content: selectedCustomerContent((customerId) => (
								<ProjectsOfCustomerDataGrid key={customerId} customerId={customerId} />
							)),
						},
						hasPermission(Permissions.Delivery.Deliveries.READ) && {
							type: "panel",
							title: i18n.t("packages"),
							content: selectedCustomerContent((customerId) => (
								<CustomerDeliveryPackagesView key={customerId} customerId={customerId} />
							)),
						},
						hasPermission(Permissions.Delivery.Deliveries.READ) && {
							type: "panel",
							title: i18n.t("deliveries"),
							content: selectedCustomerContent((customerId) => (
								<CustomerDeliveriesDataGrid key={customerId} customerId={customerId} />
							)),
						},
						hasPermission(Permissions.Sales.Invoices.READ) && {
							type: "panel",
							title: i18n.t("invoices"),
							content: selectedCustomerContent((customerId) => (
								<CustomerInvoicesDataGrid key={customerId} customerId={customerId} />
							)),
						},
						hasPermission(Permissions.Reclamations.READ) && {
							type: "panel",
							title: i18n.t("reclamations"),
							content: selectedCustomerContent((customerId) => (
								<ReclamationsOfCustomerDataGrid key={customerId} customerId={customerId} />
							)),
						},
					],
				},
			}}
		/>
	);
};
